/**
 * État de paiement du transport (lot 059): fonctions pures de l'écran de saisie.
 *
 * Le serveur reste la référence (`backend/app/core/statement_lines.py`): ces
 * fonctions ne servent qu'à l'aperçu pendant la saisie, avec les mêmes arrondis
 * (chaque ligne arrondie AVANT d'être sommée). Dès qu'un brouillon est
 * enregistré, l'écran affiche les montants que le serveur renvoie.
 *
 * Aucun appel réseau, aucun composant: tout se teste seul.
 */
import { arrondiDevise } from './formats';

/** Tolérance par voyage quand ni le dossier ni une règle ne la donnent (comme le serveur). */
export const TOLERANCE_PAR_DEFAUT = 100;

/** Un nombre saisi (virgule ou point, espaces) → nombre; vide ou invalide → `null`. */
export const nombreSaisi = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const texte = String(valeur).replace(/\s/g, '').replace(',', '.');
  // Des espaces seuls ne sont pas un zéro: `Number('')` vaut 0.
  if (texte === '') return null;
  const nombre = Number(texte);
  return Number.isFinite(nombre) ? nombre : null;
};

/** Un nombre du serveur → texte d'un champ de saisie (`null` → vide). */
export const texteChamp = (valeur) => (valeur === null || valeur === undefined ? '' : String(valeur));

/**
 * Manquant d'une ligne pour l'aperçu: celui du serveur s'il est constaté, sinon
 * quantité − dépoté quand le dépoté est saisi, sinon inconnu (`null`).
 */
export const manquantApercu = ({ quantite, manquantConnu, manquant, livre }) => {
  if (manquantConnu) return manquant === null || manquant === undefined ? null : Number(manquant);
  const depote = nombreSaisi(livre);
  if (depote === null) return null;
  return (Number(quantite) || 0) - depote;
};

/**
 * Une ligne: montant = quantité × prix; litres retenus = max(0, manquant − tolérance);
 * retenue = litres retenus × prix de retenue (0 si déjà retenue ailleurs ou manquant
 * inconnu); solde = montant − retenue − dépense.
 */
export const calculerLigne = (
  { quantite, prix, manquant, tolerance, prixRetenue, depense, dejaRetenu },
  devise = 'XOF',
) => {
  const montant = arrondiDevise((Number(quantite) || 0) * (Number(prix) || 0), devise);
  const seuil = tolerance === null || tolerance === undefined ? TOLERANCE_PAR_DEFAUT : Number(tolerance);
  const litresRetenus = manquant === null || manquant === undefined ? null : Math.max(0, Number(manquant) - seuil);
  const retenue =
    dejaRetenu || litresRetenus === null
      ? 0
      : arrondiDevise(litresRetenus * (Number(prixRetenue) || 0), devise);
  const frais = arrondiDevise(Number(depense) || 0, devise);
  return {
    montant,
    litresRetenus,
    retenue,
    depense: frais,
    solde: arrondiDevise(montant - retenue - frais, devise),
  };
};

/** Bas de l'état: Σ soldes − autres dépenses − avances = net à payer (lignes déjà arrondies). */
export const calculerTotaux = (lignes, { autresDepenses = 0, avances = 0 } = {}, devise = 'XOF') => {
  const somme = (cle) => arrondiDevise((lignes || []).reduce((total, ligne) => total + (Number(ligne[cle]) || 0), 0), devise);
  const solde = somme('solde');
  const charges = arrondiDevise(Number(autresDepenses) || 0, devise);
  const deduit = arrondiDevise(Number(avances) || 0, devise);
  return {
    lignes: (lignes || []).length,
    quantite: (lignes || []).reduce((total, ligne) => total + (Number(ligne.quantite) || 0), 0),
    montant: somme('montant'),
    retenue: somme('retenue'),
    depense: somme('depense'),
    solde,
    autresDepenses: charges,
    montantFacture: arrondiDevise(solde - charges, devise),
    avances: deduit,
    net: arrondiDevise(solde - charges - deduit, devise),
  };
};

/** Les totaux calculés par le serveur (`totals` d'un état) sous la forme de `calculerTotaux`. */
export const totauxDepuisServeur = (totaux) => ({
  lignes: Number(totaux?.lines) || 0,
  quantite: Number(totaux?.quantity) || 0,
  montant: Number(totaux?.amount) || 0,
  retenue: Number(totaux?.retention) || 0,
  depense: Number(totaux?.expenses) || 0,
  solde: Number(totaux?.balance) || 0,
  autresDepenses: Number(totaux?.other_charges) || 0,
  montantFacture: Number(totaux?.invoice_amount) || 0,
  avances: Number(totaux?.advances) || 0,
  net: Number(totaux?.net) || 0,
});

/**
 * Les lignes affichées d'un état: les trajets proposés par le serveur
 * (`eligible.trips`), plus ceux déjà portés par le brouillon qu'il ne propose
 * plus (ils restent visibles pour pouvoir être retirés).
 */
export const lignesProposees = (eligible, etat) => {
  const proposees = eligible?.trips || [];
  const connues = new Set(proposees.map((ligne) => ligne.trip_id));
  const orphelines = (etat?.lines || [])
    .filter((ligne) => !connues.has(ligne.trip_id))
    .map((ligne) => ({
      trip_id: ligne.trip_id,
      truck_id: ligne.truck_id,
      tractor_plate: ligne.tractor_plate,
      tank_plate: ligne.tank_plate,
      line_date: ligne.line_date,
      quantity: ligne.quantity,
      quantity_estimated: ligne.quantity_estimated,
      default_price: ligne.default_price ?? ligne.price,
      shortage_known: ligne.shortage_known,
      shortage_liters: ligne.shortage_liters,
      delivered_volume: ligne.delivered_volume,
      tolerance_liters: ligne.tolerance_liters,
      already_retained: ligne.already_retained,
      expenses: ligne.expenses,
      selected: true,
      orpheline: true,
    }));
  return [...proposees, ...orphelines];
};

/**
 * L'état de saisie (tout en texte, comme les champs) depuis ce que renvoie le
 * serveur: l'état enregistré (`etat`, absent pour un nouvel état) et les choix
 * possibles (`eligible`).
 */
export const saisieDepuis = (etat, eligible) => {
  const defauts = eligible?.defaults || {};
  const lignesEtat = new Map((etat?.lines || []).map((ligne) => [ligne.trip_id, ligne]));
  const prix = {};
  lignesProposees(eligible, etat).forEach((ligne) => {
    const enregistre = lignesEtat.get(ligne.trip_id);
    prix[ligne.trip_id] = texteChamp(enregistre?.price ?? ligne.default_price ?? defauts.price);
  });
  const selectionnes = new Set(
    lignesProposees(eligible, etat)
      .filter((ligne) => (etat ? lignesEtat.has(ligne.trip_id) : false))
      .map((ligne) => ligne.trip_id),
  );
  const avances = {};
  (eligible?.advances || []).forEach((avance) => {
    if (avance.selected_amount !== null && avance.selected_amount !== undefined) {
      avances[avance.payment_id] = texteChamp(avance.selected_amount);
    }
  });
  return {
    emisLe: etat?.issued_at ? String(etat.issued_at).slice(0, 10) : defauts.issued_at || '',
    prixRetenue: texteChamp(etat ? etat.retention_price : defauts.retention_price),
    selectionnes,
    prix,
    livre: {},
    charges: new Set((eligible?.charges || []).filter((c) => c.selected).map((c) => c.charge_key)),
    avances,
  };
};

/** Une ligne prête pour l'affichage: les faits du serveur + la saisie + l'aperçu des montants. */
export const ligneAffichee = (ligne, saisie, devise = 'XOF', enregistree = null) => {
  const prix = nombreSaisi(saisie.prix[ligne.trip_id]);
  const manquant = manquantApercu({
    quantite: ligne.quantity,
    manquantConnu: ligne.shortage_known,
    manquant: ligne.shortage_liters,
    livre: saisie.livre[ligne.trip_id],
  });
  const apercu = calculerLigne(
    {
      quantite: ligne.quantity,
      prix,
      manquant,
      tolerance: ligne.tolerance_liters,
      prixRetenue: nombreSaisi(saisie.prixRetenue),
      depense: ligne.expenses,
      dejaRetenu: ligne.already_retained,
    },
    devise,
  );
  // Un brouillon propre montre les chiffres du serveur, pas un recalcul local.
  const chiffres = enregistree
    ? {
        montant: enregistree.amount,
        litresRetenus: enregistree.retained_liters,
        retenue: enregistree.retention,
        depense: enregistree.expenses,
        solde: enregistree.balance,
      }
    : apercu;
  return {
    ...ligne,
    // `calculerTotaux` additionne `quantite`: sans elle, le total « Quantité » de l'aperçu valait 0.
    quantite: ligne.quantity,
    prix,
    manquant: enregistree ? enregistree.shortage_liters : manquant,
    manquantInconnu: enregistree ? !enregistree.shortage_known : manquant === null,
    ...chiffres,
  };
};

/** Montant maximum d'une avance cochée: son disponible, sans dépasser ce qui reste à payer. */
export const montantAvanceMax = (avance, resteAPayer) =>
  Math.max(0, Math.min(Number(avance.available) || 0, Number(resteAPayer) || 0));

/** Le corps de l'enregistrement d'un brouillon (création ou modification). */
export const corpsEnregistrement = ({ lignes, saisie, avances }) => {
  const choisies = lignes.filter((ligne) => saisie.selectionnes.has(ligne.trip_id));
  return {
    trip_ids: choisies.map((ligne) => ligne.trip_id),
    issued_at: saisie.emisLe || undefined,
    retention_price: nombreSaisi(saisie.prixRetenue),
    lines: choisies.map((ligne) => {
      const entree = { trip_id: ligne.trip_id, price: nombreSaisi(saisie.prix[ligne.trip_id]) };
      const livre = nombreSaisi(saisie.livre[ligne.trip_id]);
      // Le dépoté ne s'écrit qu'une fois: jamais renvoyé pour un manquant déjà constaté.
      if (!ligne.shortage_known && livre !== null) entree.delivered_volume = livre;
      return entree;
    }),
    advances: avances
      .filter((avance) => saisie.avances[avance.payment_id] !== undefined)
      .map((avance) => ({
        payment_id: avance.payment_id,
        amount: nombreSaisi(saisie.avances[avance.payment_id]) ?? 0,
      })),
    charge_keys: [...saisie.charges],
  };
};

/** Une clé d'idempotence neuve (`prefixe-uuid`). */
export const nouvelleCle = (prefixe = 'etat') => {
  const hasard =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefixe}-${hasard}`;
};

/**
 * Garde de clé: la même demande rejouée garde la même clé (aucun doublon si la
 * réponse s'est perdue), une demande différente en reçoit une neuve (le serveur
 * refuse une clé réutilisée pour un autre contenu).
 */
export const creerGardeCle = (prefixe = 'etat') => {
  let derniere = null;
  return (contenu) => {
    const signature = JSON.stringify(contenu);
    if (!derniere || derniere.signature !== signature) {
      derniere = { signature, cle: nouvelleCle(prefixe) };
    }
    return derniere.cle;
  };
};
